/**
 * Simple bridge to show toast notifications from non-React code
 * Uses the existing useToast system via CustomEvent
 * 
 * For critical alerts that need a banner (not toast), use showBannerAlert()
 */

export type NotificationType = 'success' | 'error' | 'warning' | 'info';
export type BannerAlertType = 'error' | 'warning' | 'info' | 'success';

export interface NotificationEvent {
  type: NotificationType;
  message: string;
  duration?: number;
}

export interface BannerAlertEvent {
  type: BannerAlertType;
  message: string;
  title?: string;
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  }>;
  dismissible?: boolean;
}

const NOTIFICATION_EVENT = 'app-notification';
const BANNER_ALERT_EVENT = 'app-banner-alert';

export function showNotification(type: NotificationType, message: string, duration?: number): void {
  window.dispatchEvent(new CustomEvent<NotificationEvent>(NOTIFICATION_EVENT, {
    detail: { type, message, duration }
  }));
}

/**
 * Show a banner alert (for critical errors, security alerts, etc.)
 * Banners stay visible until dismissed and are more prominent than toasts
 */
export function showBannerAlert(
  type: BannerAlertType,
  message: string,
  options?: {
    title?: string;
    actions?: Array<{
      label: string;
      onClick: () => void;
      variant?: 'primary' | 'secondary';
    }>;
    dismissible?: boolean;
  }
): void {
  window.dispatchEvent(new CustomEvent<BannerAlertEvent>(BANNER_ALERT_EVENT, {
    detail: {
      type,
      message,
      title: options?.title,
      actions: options?.actions,
      dismissible: options?.dismissible ?? true,
    }
  }));
}

export const NOTIFICATION_EVENT_NAME = NOTIFICATION_EVENT;
export const BANNER_ALERT_EVENT_NAME = BANNER_ALERT_EVENT;

export default {
  success: (msg: string, duration?: number, banner?: boolean) => {
    if (banner) {
      showBannerAlert('success', msg, { dismissible: true });
    } else {
      showNotification('success', msg, duration);
    }
  },
  error: (msg: string, duration?: number, banner?: boolean) => {
    if (banner) {
      showBannerAlert('error', msg, { dismissible: true });
    } else {
      showNotification('error', msg, duration);
    }
  },
  warning: (msg: string, duration?: number, banner?: boolean) => {
    if (banner) {
      showBannerAlert('warning', msg, { dismissible: true });
    } else {
      showNotification('warning', msg, duration);
    }
  },
  info: (msg: string, duration?: number, banner?: boolean) => {
    if (banner) {
      showBannerAlert('info', msg, { dismissible: true });
    } else {
      showNotification('info', msg, duration);
    }
  }
};